import PropTypes from 'prop-types';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { MdOutlineFileDownload } from 'react-icons/md';
import parse from 'html-react-parser';
import getCompanyOptionByPriceLinkIdApi from '@/api/get/company/company-options/get-company-option-by-price-id';
import SubmitCard from '@/components/card/submit-card-component';
import Loader from '@/components/loader/loader-component';
import Modal from '@/components/modal/modal-component';
import { useLazyQuery } from '@/hooks/use-api';
import ImageCarousel from '@/components/image-carousel/image-carousel-component';
import { ModalTitle, ModalHeader } from '@/components/modal/modal-style';
import { PrimaryOutlineButton } from '@/components/button/button-component';
import Styled from '../configuration-style';

/**
 * InfoModal component to render option information
 *
 * @param {object} props - The props object
 * @param {boolean} props.open - The modal open boolean
 * @param {Function} props.close - The close modal function
 * @param {string} props.id - The node id
 * @param {Function} props.addToSelection - The add to selection function
 * @param {boolean} props.showPrimaryButton - Whether to show the primary button
 * @returns {JSX.Element} The rendered info modal component
 */
const InfoModal = ({ open, close, id, addToSelection, showPrimaryButton }) => {
  const { state, pathname } = useLocation();
  const { id: supplierId } = useParams();
  const selectedSupplier = state?.selectedSupplier;
  const [slideIndex, setSlideIndex] = useState(0);

  const { refetch, data, loading, error } = useLazyQuery(
    getCompanyOptionByPriceLinkIdApi,
  );

  const updateSelectedSupplier = useCallback(() => {
    refetch(supplierId, id);
  }, [id, refetch, supplierId]);

  useEffect(() => {
    if (
      open &&
      !selectedSupplier &&
      pathname?.includes('/preview') &&
      supplierId
    ) {
      updateSelectedSupplier();
    }
  }, [open, pathname, selectedSupplier, supplierId, updateSelectedSupplier]);

  useEffect(() => {
    if (open && id && selectedSupplier?.id) {
      refetch(selectedSupplier?.id, id);
    }
  }, [open, id, refetch, selectedSupplier]);

  const images = useMemo(() => {
    if (!data) return [];

    const baseImages = data?.gallery
      ?.map((opl) => ({
        image: opl.image,
        alt: data?.name,
        embedUrl: null,
        order: opl.order,
      }))
      .sort((a, b) => a.order - b.order);

    const priorityImages = [];

    const thumbnail =
      (data?.option_price_link_thumbnail?.includes('http') &&
        data?.option_price_link_thumbnail) ||
      data?.thumbnail ||
      '';

    // Add thumbnail first
    // Handle historic's as well as option_price_link_thumbnail which takes priority
    if (thumbnail) {
      priorityImages.push({
        image: thumbnail,
        alt: data?.option_price_link_name || data?.name,
        embedUrl: null,
        order: 0,
      });
    }

    // Add images second
    if (priorityImages?.length) {
      priorityImages.push(...baseImages);
    }

    // Add video last (lowest priority)
    if (data?.video_link) {
      priorityImages.push({
        image: thumbnail,
        alt: data?.name,
        embedUrl: data?.video_link,
        order: priorityImages?.length,
      });
    }

    return priorityImages;
  }, [data]);

  return (
    <Modal
      hideHeader
      showFloatingCloseButton
      open={open}
      close={close}
      width="40%"
      padding="0px"
      minHeight="30vh"
      bodyStyle={{ flexDirection: 'column', padding: 0 }}
    >
      <Styled.ModalBody noPadding>
        {error && (
          <Styled.WrapperContainer>
            <p>Error </p>
          </Styled.WrapperContainer>
        )}
        {loading ? (
          <Styled.WrapperContainer>
            <Loader loading />
          </Styled.WrapperContainer>
        ) : (
          <>
            <div>
              <Styled.CarouselContainer isInModal>
                <ImageCarousel
                  images={images}
                  startIndex={slideIndex}
                  onSlide={(x) => setSlideIndex(x)}
                  showFullscreenButton={false}
                  // showNav={false}
                />
              </Styled.CarouselContainer>
            </div>
            <Styled.TitleContainer>
              <ModalHeader $padding="0px 0px 0px 20px">
                <ModalTitle data-cy="divModalTitle" $margin="0">
                  {data?.option_price_link_name || data?.name}
                </ModalTitle>
              </ModalHeader>
            </Styled.TitleContainer>
            <Styled.DescriptionContainer>
              {parse(
                data?.option_price_link_description ||
                  data?.description ||
                  '<p>No description available</p>',
              )}
            </Styled.DescriptionContainer>
            {data?.brochure_link && (
              <Styled.DescriptionContainer>
                <PrimaryOutlineButton
                  title="Brochure"
                  margin="0 0 10px 10px"
                  icon={<MdOutlineFileDownload />}
                  type="button"
                  onClick={() => window.open(data?.brochure_link, '_blank')}
                />
              </Styled.DescriptionContainer>
            )}
          </>
        )}
      </Styled.ModalBody>
      <SubmitCard
        padding="0"
        showPrimaryButton={showPrimaryButton}
        primaryButtonTitle="Add to Selection"
        primaryButtonType="submit"
        primaryButtonDisabled={false}
        primaryButtonWidth="160px"
        submitClick={addToSelection}
        secondaryButtonType="button"
        secondaryButtonDisabled={false}
        secondaryButtonWidth="fit-content"
        secondaryButtonTitle="Cancel"
        submitClickSecondary={close}
      />
    </Modal>
  );
};
InfoModal.defaultProps = {
  addToSelection: () => {},
  showPrimaryButton: true,
};

InfoModal.propTypes = {
  id: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  addToSelection: PropTypes.func,
  close: PropTypes.func.isRequired,
  showPrimaryButton: PropTypes.bool,
};

export default InfoModal;
